import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import { useCallback, useMemo, useState, useEffect } from 'react';
import '../../../constants/icons/index.mjs';
import '../../../contexts/MicrositeMenuContext/index.mjs';
import { MenuItemId, START_HERE_ID } from '../../../contexts/MicrositeMenuContext/mapping.mjs';
import { useMicrositeMenuTranslations } from '../../../contexts/MicrositeMenuContext/translations.mjs';
import { useFooterContent, FooterLinkId, FooterHeaderId } from '../../Footer/hooks/useFooterContent.mjs';
import { Container, InnerWrap, SectionItem, SectionItemsGroup, SectionItemLabel, Badge, SectionItemArrowButton, Chevron, SecondLevelMenu } from './MicrositeMobileMenu.styles.mjs';
import { useMicrositeMenu } from '../../../contexts/MicrositeMenuContext/context.mjs';
import _externalAssets_BAXELvSy__default from "../../../assets/collapseArrow.svg";
"use strict";
const MicrositeMobileMenu = ({
  menu,
  isOpen,
  onMenuItemClick,
  onModalClose
}) => {
  const {
    language
  } = useMicrositeMenu();
  const {
    formatMessage
  } = useMicrositeMenuTranslations(language);
  const {
    footerContent,
    socialContent,
    miscLinks
  } = useFooterContent();
  const recursivelyTranslate = useCallback(item => {
    return {
      ...item,
      id: item.id,
      url: item.url,
      isActive: item.active,
      isNew: item.isNew ? formatMessage("commons.menu.label.new") : void 0,
      label: formatMessage(item.label),
      items: item.items?.map(recursivelyTranslate),
      target: item.isExternalUrl ? "_blank" : void 0
    };
  }, [formatMessage]);
  const filterHowItWorks = useCallback(({
    id
  }) => id === MenuItemId.HowItWorks, []);
  const filterDiscardedStartHereItems = useCallback(({
    id
  }) => id !== MenuItemId.HowItWorks && id !== MenuItemId.SiteMap, []);
  const filterDupesFromStartHere = useCallback(({
    id
  }) => id !== FooterLinkId.Blog && id !== FooterLinkId.CaseStudies, []);
  const {
    preparedMenu,
    discardedStartHereItems
  } = useMemo(() => {
    let discardedItems = [];
    const menuResult = (menu?.items ?? []).reduce((navigation, submenu) => {
      const translatedSubmenu = recursivelyTranslate(submenu);
      if (translatedSubmenu.id === START_HERE_ID) {
        const submenuItems = translatedSubmenu.items ?? [];
        const howItWorks = submenuItems.find(filterHowItWorks);
        discardedItems = submenuItems.filter(filterDiscardedStartHereItems);
        return howItWorks ? [...navigation, howItWorks] : navigation;
      }
      return [...navigation, translatedSubmenu];
    }, []);
    return {
      preparedMenu: menuResult,
      discardedStartHereItems: discardedItems
    };
  }, [menu?.items, recursivelyTranslate, filterHowItWorks, filterDiscardedStartHereItems]);
  const normalizedFooterContent = useMemo(() => {
    const topLinks = miscLinks.map(item => ({
      id: item.id,
      label: item.label,
      target: item?.target,
      url: item.src
    }));
    const footerLinks = Object.values(footerContent).map(section => {
      const shouldFilter = section.header.id === FooterHeaderId.Company;
      const filteredItems = shouldFilter ? section.items.filter(filterDupesFromStartHere) : section.items;
      return {
        id: section.header.id,
        label: section.header.label,
        items: filteredItems.map(item => ({
          id: item.id,
          label: item.label,
          target: item?.target,
          url: item.src
        }))
      };
    });
    const social = {
      id: socialContent.header.id,
      label: socialContent.header.label,
      items: socialContent.items.map(link => ({
        id: link.id,
        label: link.label,
        target: link?.target,
        url: link.src
      }))
    };
    const extraFromStartHere = discardedStartHereItems.map(item => ({
      id: item.id,
      label: item.label,
      items: item.items
    }));
    return [{
      id: "more-from-ui",
      label: formatMessage("menu.label.moreFromUi"),
      items: [...extraFromStartHere, ...topLinks, ...footerLinks, social]
    }];
  }, [footerContent, formatMessage, miscLinks, socialContent.header.id, socialContent.header.label, socialContent.items, discardedStartHereItems, filterDupesFromStartHere]);
  return /* @__PURE__ */jsx(Container, {
    children: /* @__PURE__ */jsx(InnerWrap, {
      children: /* @__PURE__ */jsx(MobileMenu, {
        items: [...preparedMenu, ...normalizedFooterContent],
        onMenuItemClick,
        onModalClose,
        isOpen
      })
    })
  });
};
const MobileMenu = ({
  items,
  level = 1,
  isOpen,
  onMenuItemClick,
  onModalClose
}) => {
  const [expandedItemId, setExpandedItemId] = useState(null);
  useEffect(() => {
    if (!isOpen) {
      setExpandedItemId(null);
    }
  }, [isOpen]);
  useEffect(() => {
    const isAnyChildActive = items.find(({
      items: childItems
    }) => childItems?.some(child => child.isActive));
    if (isAnyChildActive) {
      setExpandedItemId(isAnyChildActive.id);
    }
  }, [items]);
  const handleItemClick = useCallback((event, item) => {
    if (item?.items?.length) {
      event.stopPropagation();
      setExpandedItemId(currentId => item.id === currentId ? null : item.id);
    } else if (item?.url) {
      onMenuItemClick(event, item.url);
      onModalClose();
    }
  }, [onMenuItemClick, onModalClose]);
  const SectionItem$1 = useCallback(({
    item,
    children
  }) => item.target === "_blank" ? /* @__PURE__ */jsx(SectionItem, {
    as: "a",
    $level: level,
    href: item.url,
    target: "_blank",
    rel: "noopener noreferrer",
    children
  }) : /* @__PURE__ */jsx(SectionItem, {
    $level: level,
    onClick: event => handleItemClick(event, item),
    children
  }), [handleItemClick, level]);
  const isActiveChildItem = useCallback(item => {
    return item.isActive && !item.items?.length;
  }, []);
  return /* @__PURE__ */jsx(Fragment, {
    children: items.map(item => /* @__PURE__ */jsxs(SectionItemsGroup, {
      children: [/* @__PURE__ */jsxs(SectionItem$1, {
        item,
        children: [/* @__PURE__ */jsx(SectionItemLabel, {
          $level: level,
          $active: item.id === expandedItemId || isActiveChildItem(item),
          children: item.label
        }), !!item.isNew && /* @__PURE__ */jsx(Badge, {
          children: item.isNew
        }), item?.items && /* @__PURE__ */jsx(SectionItemArrowButton, {
          $active: item.id === expandedItemId,
          children: /* @__PURE__ */jsx(Chevron, {
            src: _externalAssets_BAXELvSy__default,
            width: 13,
            height: 8,
            $isRotated: true
          })
        })]
      }), item.id === expandedItemId && /* @__PURE__ */jsx(SecondLevelMenu, {
        $level: level,
        children: /* @__PURE__ */jsx(MobileMenu, {
          items: item.items ?? [],
          level: level + 1,
          onMenuItemClick,
          onModalClose
        })
      })]
    }, item.id))
  });
};
export { MicrositeMobileMenu };
//# sourceMappingURL=MicrositeMobileMenu.mjs.map
